import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_syndic-app-1/artifacts/1ku8mjse_Logo%20Syndicappli_White_2022.png";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00A88D 100%)' }}
      data-testid="login-page"
    >
      <div className="w-full max-w-5xl flex flex-col items-center">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="SyndicAppli" 
            className="h-12 w-auto mx-auto"
            data-testid="login-logo"
          />
          <p className="text-white/80 text-sm mt-2 font-medium">La Vie de votre Copro</p>
        </div>

        {/* Description */}
        <p className="text-white/90 text-center max-w-xl mb-8 text-sm leading-relaxed px-4">
          Bienvenue sur SyndicAppli, votre plateforme de gestion de copropriété. 
          Connectez-vous pour accéder à votre espace personnel.
        </p>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm" data-testid="login-card">
          <h2 className="text-xl font-bold text-slate-800 mb-6 text-center">Connexion</h2>
          
          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 focus:border-[#00D3A8] transition-all placeholder:text-slate-400"
                  placeholder="votre@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2" htmlFor="password">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 focus:border-[#00D3A8] transition-all placeholder:text-slate-400"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white font-medium py-3 px-4 rounded-full transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:opacity-95"
              style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Connexion...</span>
                </>
              ) : (
                <span>Se connecter</span>
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <a 
              href="/forgot-password" 
              className="text-[#00D3A8] hover:text-[#00C7A1] text-sm font-medium transition-colors"
              data-testid="forgot-password-link"
            >
              J'ai oublié mon mot de passe
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-white/50 text-xs mt-8">
          © 2026 SyndicAppli - Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default Login;
